/*
  FAQs — the "view all" toggle and the column pinning for
  sections/subscription-faqs.liquid.

  The rows past the section's "Questions shown" setting are rendered with the
  `hidden` attribute on them, so they are in the page for a reader without
  JavaScript and for search engines while staying out of the columns. This takes
  that attribute off and puts it back, swaps the button's label with the one the
  theme editor set for the open state, and holds the column split still so that
  neither an answer opening nor the button firing can shuffle a question from
  one column into the next.
*/
(function () {
  const READY = 'subscriptionFaqsReady';

  // One entry per section on the page, so the resize and web-font passes can
  // reach a split that was measured when the section was set up.
  const sections = [];

  function itemsOf(list) {
    return Array.prototype.slice.call(list.children);
  }

  function clearBreaks(items) {
    items.forEach(function (item) {
      delete item.dataset.faqBreak;
    });
  }

  // Appending an element that is already in the list moves it, so walking the
  // wanted order and appending each item in turn is enough to sort the list.
  function order(list, items) {
    items.forEach(function (item) {
      list.appendChild(item);
    });
  }

  /*
    Where the columns actually break, read back off the page.

    Balanced columns are re-measured by the browser every time the list changes
    height, which walks the question at the foot of a column over into the next
    one while the reader is looking at it. Nothing in the multi-column
    properties holds it: `column-fill: auto` wants a height this list cannot be
    given, and the height it balances to is exactly what an open answer changes.

    So the split the browser arrives at on its own is taken down once — the item
    whose left edge has moved on is the first of the next column — and from then
    on it is imposed with a forced break rather than measured again. The
    held-back rows are put away for the measurement, so the split is always the
    one the reader meets first, whether or not the button has been pressed.
  */
  function measure(state) {
    const wasHidden = state.extras.map(function (item) {
      return item.hidden;
    });

    state.extras.forEach(function (item) {
      item.hidden = true;
    });

    order(state.list, state.base);
    clearBreaks(state.base);

    const shown = state.base.filter(function (item) {
      return !item.hidden;
    });

    // Below 750px the stylesheet sets no `column-count` and the questions are
    // one under the other, so the whole list is the one column.
    const columns = parseInt(window.getComputedStyle(state.list).columnCount, 10);

    if (!(columns > 1) || shown.length < 2) {
      state.columns = [shown];
    } else {
      const groups = [[]];
      let previous = shown[0].getBoundingClientRect().left;

      shown.forEach(function (item) {
        const left = item.getBoundingClientRect().left;

        // A pixel or so apart is sub-pixel rounding inside one column; a column
        // break is a whole gutter's worth.
        if (Math.abs(left - previous) > 1) groups.push([]);

        groups[groups.length - 1].push(item);
        previous = left;
      });

      state.columns = groups;
    }

    state.extras.forEach(function (item, index) {
      item.hidden = wasHidden[index];
    });
  }

  /*
    The list runs one column after another in source order, so a held-back row
    let out at the end of the markup would land at the foot of the last column
    and leave the others as they were. They are dealt out between the columns
    instead and moved into the list behind the questions already standing in
    each one, which is what lets a column grow downwards while every question
    above the new ones keeps the place it was read in.
  */
  function apply(state, isOpen) {
    const columns = state.columns.map(function (group) {
      return group.slice();
    });

    if (isOpen && state.extras.length) {
      let dealt = 0;

      columns.forEach(function (group, index) {
        const left = state.extras.length - dealt;
        const share = Math.ceil(left / (columns.length - index));

        state.extras.slice(dealt, dealt + share).forEach(function (item) {
          group.push(item);
        });

        dealt += share;
      });
    }

    const wanted = columns.reduce(function (all, group) {
      return all.concat(group);
    }, []);

    // Closed again, the held-back rows go back to the end of the markup they
    // were rendered in rather than being left standing between the columns.
    order(state.list, isOpen ? wanted : wanted.concat(state.extras));

    clearBreaks(state.base);

    // The last column ends where the list does, so it is left without a break.
    columns.slice(0, -1).forEach(function (group) {
      const last = group[group.length - 1];
      if (last) last.dataset.faqBreak = '';
    });
  }

  function isOpenState(state) {
    return !!state.button && state.button.getAttribute('aria-expanded') === 'true';
  }

  function relayout(state) {
    measure(state);
    apply(state, isOpenState(state));
  }

  function relayoutAll() {
    sections.forEach(function (state) {
      if (state.list.isConnected) relayout(state);
    });
  }

  function initSection(section) {
    // shopify:section:load fires on the editor's own re-renders as well, so a
    // section that is already wired up is left alone.
    if (section.dataset[READY] === 'true') return;

    const list = section.querySelector('.subscription-faqs__list');
    if (!list) return;

    section.dataset[READY] = 'true';

    const state = {
      list: list,
      base: itemsOf(list),
      extras: Array.prototype.slice.call(section.querySelectorAll('[data-faq-extra]')),
      button: section.querySelector('[data-faq-toggle]'),
      columns: []
    };

    sections.push(state);
    relayout(state);

    if (!state.button || !state.extras.length) return;

    state.button.addEventListener('click', function () {
      const isOpen = isOpenState(state);

      state.extras.forEach(function (item) {
        item.hidden = isOpen;

        // A question closed away again takes its answer with it, so reopening
        // the list does not bring back a row that was left expanded.
        if (isOpen) {
          const details = item.querySelector('details');
          if (details) details.open = false;
        }
      });

      state.button.setAttribute('aria-expanded', isOpen ? 'false' : 'true');

      const label = isOpen ? state.button.dataset.labelMore : state.button.dataset.labelLess;
      if (label) state.button.textContent = label;

      // The split itself is not measured again here: the rows just let out are
      // dealt into the columns the reader is already looking at.
      apply(state, !isOpen);

      // Collapsing can leave the button above the fold it was pressed from, so
      // the list it belongs to is brought back into view with it.
      if (isOpen) {
        state.button.scrollIntoView({block: 'nearest', behavior: 'smooth'});
      }
    });
  }

  function initAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-subscription-faqs]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // A heading font arriving after the first paint re-wraps the questions, so
  // the split measured against the fallback face is taken again.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(relayoutAll);
  }

  // The number of columns and the width of each one both come off the viewport,
  // so a resize is the one moment the split has to be given up and read again.
  let resizeFrame = null;
  window.addEventListener('resize', function () {
    if (resizeFrame) window.cancelAnimationFrame(resizeFrame);

    resizeFrame = window.requestAnimationFrame(function () {
      resizeFrame = null;
      relayoutAll();
    });
  });

  // Wire up the one section the theme editor just re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
